import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CalendarToday,
  Download,
  FilterList,
  Inventory2Outlined,
  Refresh,
  Savings,
  Receipt,
  TrendingUp,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useMutation, useQuery } from '@tanstack/react-query';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { ConfirmDialog, PageContainer, PageHeader } from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { useStorePlatform } from '../hooks/useStorePlatform';
import { platformLabel, platformSupportsOrderSync } from '../utils/storePlatform';
import { tokens } from '../theme/tokens';
import { useOrganizations } from '../contexts/OrganizationContext';
import { RebateLineSerialsCell, postSerialClaims, type RebateReportSoldItem } from '../components/rebateReport/RebateLineSerialsCell';
import { RebateLineSoldUnits, type RebateReportSoldUnit } from '../components/rebateReport/RebateLineSoldUnits';
import { TrialFeatureTitle } from '../components/trial/TrialFeatureBadge';

const mobileLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '.06em',
  color: tokens.ink.faint,
  display: 'block',
};

interface RebateReportLine {
  sku: string;
  upc?: string;
  productName: string;
  brandName?: string;
  rebateBrand?: string;
  rebateManager?: string;
  matchedBy: 'sku' | 'upc';
  inCatalog: boolean;
  rebateProgramId?: string | null;
  rebatePricingRowId?: string;
  bundleRuleId?: string;
  rebateType: 'instant_rebate' | 'trade_credit' | 'mail_in_rebate' | 'bundle';
  rebatePerUnit: number;
  unitsSold: number;
  totalRebate: number;
  /** Cents; present only when the funding-split flag is on. */
  vendorFundedPerUnit?: number;
  storeFundedPerUnit?: number;
  totalVendorFunded?: number;
  totalStoreFunded?: number;
  /** Cents; present only with the rebate tax report flag on. null = a sold unit carried no tax rate. */
  taxOwedPerUnit?: number | null;
  totalTaxOwed?: number | null;
  programStart?: string;
  programEnd?: string;
  agreementNumber?: string;
  region?: string;
  orderNumbers: string[];
  /** Present only with the serial capture flag on: serials of the claimed units and the sold lines behind the line. */
  serialNumbers?: string[];
  soldItems?: RebateReportSoldItem[];
  /** Present only with the condition pricing flag on and a per-unit listing behind the line. */
  soldUnits?: RebateReportSoldUnit[];
}

interface RebateReportGroup {
  name: string;
  unitsSold: number;
  totalRebate: number;
  programCount: number;
  lineCount: number;
}

interface RebateReportSummary {
  period: { start: string; end: string };
  totalUnits: number;
  totalRebateAmount: number;
  totalVendorFunded?: number;
  totalStoreFunded?: number;
  /** Present only with the rebate tax report flag on. */
  totalTaxOwed?: number;
  taxRule?: { treatment: 'pre_rebate' | 'post_rebate' | 'unknown'; source: string; overridden: boolean; state: string | null };
  linesWithoutTaxRate?: number;
  /** Present only with the serial capture flag on. */
  serialCapture?: { unitsSold: number; unitsWithSerial: number; serialsClaimSubmitted?: number; serialsClaimPaid?: number };
  programCount: number;
  lineCount: number;
  unmatchedUnits: number;
  byManager: RebateReportGroup[];
  byBrand: RebateReportGroup[];
  lines: RebateReportLine[];
}

interface RebateReportSources {
  managers: Array<{ name: string; programCount: number }>;
  brands: Array<{ name: string; programCount: number }>;
}

const fmt = (cents: number) =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const num = (n: number) => n.toLocaleString();

/** Compact program window: "Sep 1 – Sep 30", with the year only when it is not this one. */
const windowStr = (startIso?: string, endIso?: string) => {
  if (!startIso && !endIso) return '—';
  const start = startIso ? dayjs(startIso) : null;
  const end = endIso ? dayjs(endIso) : null;
  if (start && end && start.year() === end.year()) {
    const year = end.year() === dayjs().year() ? '' : `, ${end.year()}`;
    return `${start.format('MMM D')} – ${end.format('MMM D')}${year}`;
  }
  const fmtFull = (d: dayjs.Dayjs | null) => (d ? d.format('MMM D, YYYY') : '—');
  return `${fmtFull(start)} – ${fmtFull(end)}`;
};

const REBATE_TYPE_LABELS: Record<RebateReportLine['rebateType'], string> = {
  instant_rebate: 'Instant Rebate',
  trade_credit: 'Trade Credit',
  mail_in_rebate: 'Mail-In Rebate',
  bundle: 'Bundle Rebate',
};

export const RebateReportPage: React.FC = () => {
  const isMobile = useIsMobile();
  const { impersonatedOrg } = useImpersonation();
  const { isAdmin } = useAuth();
  const storePlatform = useStorePlatform();
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(30, 'day'));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());
  const [managerFilter, setManagerFilter] = useState<string>('');
  const [brandFilter, setBrandFilter] = useState<string>('');
  const [exporting, setExporting] = useState(false);
  const [markOpen, setMarkOpen] = useState(false);
  const [claimReference, setClaimReference] = useState('');

  const orgScope = impersonatedOrg?.id ?? 'self';

  // Rebates match against synced orders, which only Shopify and Lightspeed integrations
  // provide. Say what is actually true for this store's platform instead of assuming Shopify.
  const orderSyncHint = useMemo(() => {
    if (storePlatform.loading) return 'Rebates are matched against your synced orders by SKU and UPC.';
    if (!storePlatform.hasIntegration) {
      return 'Rebates are matched against your synced orders by SKU and UPC. Connect a Shopify or Lightspeed integration on the Integrations page to sync orders.';
    }
    if (!storePlatform.hasOrderSyncPlatform) {
      return `Rebates are matched against your synced orders by SKU and UPC. Order sync is not available for ${storePlatform.platformsLabel} yet — connect Shopify or Lightspeed to sync orders.`;
    }
    const syncing = storePlatform.platforms.filter(platformSupportsOrderSync).map((p) => platformLabel(p));
    const names = syncing.length > 1 ? `${syncing.slice(0, -1).join(', ')} and ${syncing[syncing.length - 1]}` : syncing[0];
    return `Rebates are matched against your synced ${names} orders by SKU and UPC — make sure Order Sync is enabled on your ${names} integration.`;
  }, [storePlatform.loading, storePlatform.hasIntegration, storePlatform.hasOrderSyncPlatform, storePlatform.platformsLabel, storePlatform.platforms]);

  const dateParams = useMemo(() => ({
    startDate: startDate.format('YYYY-MM-DD'),
    endDate: endDate.format('YYYY-MM-DD'),
  }), [startDate, endDate]);

  const reportParams = useMemo(() => ({
    ...dateParams,
    ...(managerFilter ? { rebateManager: managerFilter } : {}),
    ...(brandFilter ? { rebateBrand: brandFilter } : {}),
  }), [dateParams, managerFilter, brandFilter]);

  const qs = (params: Record<string, string>) =>
    Object.entries(params).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');

  const {
    data: sources,
  } = useQuery<RebateReportSources>({
    queryKey: ['rebate-report-sources', orgScope, dateParams],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/store/sell-through/rebate-report/sources?${qs(dateParams)}`, { headers });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(json?.error?.message || json?.error || `Failed to load rebate sources (HTTP ${res.status})`);
      }
      return (json?.data || { managers: [], brands: [] }) as RebateReportSources;
    },
    retry: false,
    staleTime: 5 * 60_000,
  });

  const {
    data: report,
    isLoading,
    refetch,
    error,
  } = useQuery<RebateReportSummary>({
    queryKey: ['rebate-report', orgScope, reportParams],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/store/sell-through/rebate-report?${qs(reportParams)}`, { headers });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(json?.error?.message || json?.error || `Failed to load rebate report (HTTP ${res.status})`);
      }
      return (json?.data || json) as RebateReportSummary;
    },
    retry: false,
    staleTime: 60_000,
  });

  const handleExport = async () => {
    setExporting(true);
    try {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/store/sell-through/rebate-report/export?${qs(reportParams)}`, { headers });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rebate-report-${reportParams.startDate}-to-${reportParams.endDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // download errors surface via the disabled state / user retry
    } finally {
      setExporting(false);
    }
  };

  const { t } = useAppTranslation();
  const { formatCurrency } = useLocale();
  // Present only while the rebate tax report flag is on for this store.
  const taxRule = report?.taxRule;
  const showTax = taxRule !== undefined;
  const taxCell = (cents: number | null | undefined, line: RebateReportLine) =>
    line.rebateType !== 'instant_rebate' || cents === undefined
      ? '—'
      : cents === null
        ? t('store.rebateTax.noRate')
        : formatCurrency(cents / 100);
  const hasData = Boolean(report && report.lineCount > 0);
  // Present only while the funding-split flag is on for this store.
  const showSplit = report?.totalVendorFunded !== undefined;
  // Present only while the serial capture flag is on for this store; the cell writes
  // serials to the sold lines, which an owner, admin or manager may do.
  const showSerials = report?.serialCapture !== undefined;
  const { actingOrganization } = useOrganizations();
  const canEditSerials = isAdmin || (actingOrganization ? ['owner', 'admin', 'manager'].includes(actingOrganization.role) : false);
  const serialsCell = (line: RebateReportLine) => (
    <RebateLineSerialsCell
      productName={line.productName}
      serialNumbers={line.serialNumbers ?? []}
      soldItems={line.soldItems ?? []}
      unitsSold={line.unitsSold}
      canEdit={canEditSerials}
      onSaved={() => { void refetch(); }}
    />
  );
  // Slice 2 (MOT-374): every serial on the report with no claim yet under its line's program
  // (MOT-420), per sold line and program, so the store can mark them submitted once the claim
  // export has gone to the vendor.
  const unclaimedItems = useMemo(() => {
    const byLine = new Map<string, { orderItemId: string; programKey: string; serials: Set<string> }>();
    for (const line of report?.lines ?? []) {
      for (const item of line.soldItems ?? []) {
        const claimed = new Set((item.serialClaims ?? []).map((c) => c.serial));
        const open = item.serialNumbers.filter((s) => !claimed.has(s));
        if (open.length === 0) continue;
        const key = `${item.orderItemId}\u0000${item.programKey}`;
        const entry = byLine.get(key) ?? { orderItemId: item.orderItemId, programKey: item.programKey, serials: new Set<string>() };
        open.forEach((s) => entry.serials.add(s));
        byLine.set(key, entry);
      }
    }
    return Array.from(byLine.values()).map(({ orderItemId, programKey, serials }) => ({ orderItemId, programKey, serials: Array.from(serials) }));
  }, [report]);
  const unclaimedCount = unclaimedItems.reduce((sum, i) => sum + i.serials.length, 0);
  const { showToast } = useToast();
  const markSubmitted = useMutation({
    mutationFn: () =>
      postSerialClaims(
        { status: 'submitted', reference: claimReference.trim() || null, items: unclaimedItems },
        t('store.rebateSerials.markSubmitted.error'),
      ),
    onSuccess: (result) => {
      setMarkOpen(false);
      setClaimReference('');
      void refetch();
      showToast(
        result.skipped.length > 0
          ? t('store.rebateSerials.markSubmitted.partial', { count: result.updated, skipped: result.skipped.length })
          : t('store.rebateSerials.markSubmitted.done', { count: result.updated }),
        result.skipped.length > 0 ? 'warning' : 'success',
      );
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });
  const cardSx = { height: '100%' };

  const kpi = (
    icon: React.ReactNode,
    bg: string,
    label: string,
    value: string,
    caption?: string,
  ) => (
    <Card sx={cardSx}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: caption ? 1 : 0 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: 2, backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2 }}>
            {icon}
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>{label}</Typography>
            {isLoading ? (
              <Skeleton width={90} height={32} />
            ) : (
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>{value}</Typography>
            )}
          </Box>
        </Box>
        {caption && !isLoading && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{caption}</Typography>
        )}
      </CardContent>
    </Card>
  );

  const groupTable = (title: string, groups: RebateReportGroup[]) => (
    <Paper sx={{ p: 2, flex: 1, minWidth: 280 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
      {groups.length === 0 ? (
        <Typography variant="caption" color="text.secondary">No claimable rebates in this period.</Typography>
      ) : (
        <TableContainer>
          <Table size="small" sx={{ '& td, & th': { py: 0.5, fontSize: '0.82rem' } }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align="right">Programs</TableCell>
                <TableCell align="right">Units</TableCell>
                <TableCell align="right">Claimable</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((g) => (
                <TableRow key={g.name} hover>
                  <TableCell>{g.name}</TableCell>
                  <TableCell align="right">{num(g.programCount)}</TableCell>
                  <TableCell align="right">{num(g.unitsSold)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(g.totalRebate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <PageContainer>
        <PageHeader
          title={<TrialFeatureTitle title="Rebate Report" feature="rebateTracking" />}
          description="Match your sold items against vendor rebate programs to see what you can submit for rebate."
        />

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <FilterList sx={{ color: 'text.secondary' }} />
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(v) => v && setStartDate(v)}
            slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(v) => v && setEndDate(v)}
            slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
          />
          <TextField
            select
            size="small"
            label="Submitted via"
            value={managerFilter}
            onChange={(e) => setManagerFilter(e.target.value)}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All</MenuItem>
            {(sources?.managers || []).map((m) => (
              <MenuItem key={m.name} value={m.name}>
                {m.name} ({m.programCount})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Brand"
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All</MenuItem>
            {(sources?.brands || []).map((b) => (
              <MenuItem key={b.name} value={b.name}>
                {b.name} ({b.programCount})
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            size="small"
            startIcon={<Refresh />}
            onClick={() => refetch()}
            disabled={isLoading}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <Download />}
            onClick={handleExport}
            disabled={exporting || !hasData}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
          {showSerials && canEditSerials && (
            <Tooltip title={unclaimedCount === 0 ? t('store.rebateSerials.markSubmitted.none') : ''}>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setMarkOpen(true)}
                  disabled={unclaimedCount === 0 || markSubmitted.isPending}
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                  {t('store.rebateSerials.markSubmitted.button')}
                </Button>
              </span>
            </Tooltip>
          )}
          {showSerials && report?.serialCapture && (
            <Typography variant="caption" color="text.secondary" sx={{ width: '100%', textAlign: 'right' }}>
              {t('store.rebateSerials.claim.summary', {
                submitted: report.serialCapture.serialsClaimSubmitted ?? 0,
                paid: report.serialCapture.serialsClaimPaid ?? 0,
              })}
            </Typography>
          )}
        </Paper>

        <ConfirmDialog
          open={markOpen}
          title={t('store.rebateSerials.markSubmitted.title')}
          message={t('store.rebateSerials.markSubmitted.message', { count: unclaimedCount })}
          preview={
            <TextField
              size="small"
              fullWidth
              label={t('store.rebateSerials.markSubmitted.reference')}
              helperText={t('store.rebateSerials.markSubmitted.referenceHelp')}
              value={claimReference}
              onChange={(e) => setClaimReference(e.target.value)}
              inputProps={{ maxLength: 120 }}
              sx={{ mt: 1 }}
            />
          }
          confirmLabel={t('store.rebateSerials.markSubmitted.confirm')}
          cancelLabel={t('common.cancel')}
          loading={markSubmitted.isPending}
          confirmDisabled={unclaimedCount === 0}
          onConfirm={() => markSubmitted.mutate()}
          onClose={() => setMarkOpen(false)}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            Unable to load the rebate report. Please try again or contact support if the issue persists.
            {isAdmin && (
              <Box component="details" sx={{ mt: 1, fontSize: '0.85em', opacity: 0.8 }}>
                <summary style={{ cursor: 'pointer' }}>Technical details</summary>
                <code style={{ display: 'block', marginTop: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {error instanceof Error ? error.message : String(error)}
                </code>
              </Box>
            )}
          </Alert>
        )}

        {showTax && taxRule && (
          <Alert severity="info" sx={{ mb: 3 }} data-testid="rebate-tax-note">
            {t(`store.rebateTax.rule.${taxRule.treatment}${taxRule.overridden ? 'Override' : ''}`, { state: taxRule.state || '' })}
            {taxRule.source ? ` ${t('store.rebateTax.source', { source: taxRule.source })}` : ''}
            {taxRule.treatment === 'pre_rebate'
              ? ` ${t('store.rebateTax.total', { amount: formatCurrency((report?.totalTaxOwed ?? 0) / 100) })}`
              : ''}
            {taxRule.treatment === 'pre_rebate' && (report?.linesWithoutTaxRate ?? 0) > 0
              ? ` ${t('store.rebateTax.missingRates', { count: report?.linesWithoutTaxRate ?? 0 })}`
              : ''}
          </Alert>
        )}

        {/* KPI Cards */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 3,
          mb: 4,
        }}>
          {kpi(
            <Savings sx={{ fontSize: 22, color: 'success.main' }} />,
            'rgba(76, 175, 80, 0.12)',
            'Total Claimable',
            report ? fmt(report.totalRebateAmount) : '$0.00',
            showSplit && report
              ? `Vendor ${fmt(report.totalVendorFunded ?? 0)} · Store ${fmt(report.totalStoreFunded ?? 0)}`
              : undefined,
          )}
          {kpi(
            <TrendingUp sx={{ fontSize: 22, color: 'info.main' }} />,
            'rgba(0, 172, 193, 0.12)',
            'Claimable Units',
            report ? num(report.totalUnits) : '0',
          )}
          {kpi(
            <Receipt sx={{ fontSize: 22, color: 'warning.main' }} />,
            'rgba(255, 152, 0, 0.12)',
            'Rebate Programs',
            report ? num(report.programCount) : '0',
            report ? `${num(report.lineCount)} product lines` : undefined,
          )}
          {kpi(
            <Inventory2Outlined sx={{ fontSize: 22, color: 'text.secondary' }} />,
            'rgba(0, 0, 0, 0.06)',
            'Units w/o Rebate',
            report ? num(report.unmatchedUnits) : '0',
            'Sold units matching no program',
          )}
        </Box>

        {/* Breakdowns */}
        {hasData && (
          <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
            {groupTable('By submitted via', report!.byManager)}
            {groupTable('By Brand', report!.byBrand)}
          </Box>
        )}

        {/* Claimable lines */}
        <Paper sx={{ overflow: 'hidden' }}>
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
              Claimable Rebates
            </Typography>
            <Chip
              icon={<CalendarToday sx={{ fontSize: 14 }} />}
              label={`${startDate.format('MMM D')} – ${endDate.format('MMM D, YYYY')}`}
              size="small"
              variant="outlined"
            />
          </Box>
          {isMobile ? (
            isLoading ? (
              <Stack sx={{ p: 2 }} spacing={1.5}>
                {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} height={96} />)}
              </Stack>
            ) : !hasData ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No sold items matched a rebate program in this period.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {orderSyncHint}
                </Typography>
              </Box>
            ) : (
              <Stack divider={<Divider />}>
                {report!.lines.map((line) => (
                  <Box key={`${line.bundleRuleId ?? line.rebatePricingRowId ?? line.rebateProgramId}-${line.sku}-${line.rebatePerUnit}-${line.storeFundedPerUnit ?? 0}`} sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2">{line.productName}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                          <Typography variant="caption" color="text.secondary">
                            {line.sku || line.upc || '—'}
                          </Typography>
                          {!line.inCatalog && (
                            <Tooltip title="This product isn't in the Motzu catalog — matched directly from the rebate sheet">
                              <Chip label="Not in catalog" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                            </Tooltip>
                          )}
                        </Box>
                        <RebateLineSoldUnits units={line.soldUnits} />
                      </Box>
                      <Chip label={REBATE_TYPE_LABELS[line.rebateType]} size="small" variant="outlined" sx={{ flexShrink: 0 }} />
                    </Stack>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mt: 1.5 }}>
                      <Box>
                        <Typography component="span" sx={mobileLabelSx}>Rebate / Unit</Typography>
                        <Typography variant="body2">{fmt(line.rebatePerUnit)}</Typography>
                      </Box>
                      <Box>
                        <Typography component="span" sx={mobileLabelSx}>Units</Typography>
                        <Typography variant="body2">{num(line.unitsSold)}</Typography>
                      </Box>
                      <Box>
                        <Typography component="span" sx={mobileLabelSx}>Total Claimable</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmt(line.totalRebate)}</Typography>
                      </Box>
                      {showSplit && (
                        <Box>
                          <Typography component="span" sx={mobileLabelSx}>Vendor / Store Funded</Typography>
                          <Typography variant="body2">
                            {fmt(line.totalVendorFunded ?? line.totalRebate)} / {fmt(line.totalStoreFunded ?? 0)}
                          </Typography>
                        </Box>
                      )}
                      {showTax && line.rebateType === 'instant_rebate' && line.totalTaxOwed !== undefined && (
                        <Box>
                          <Typography component="span" sx={mobileLabelSx}>{t('store.rebateTax.column')}</Typography>
                          <Typography variant="body2">{taxCell(line.totalTaxOwed, line)}</Typography>
                        </Box>
                      )}
                      {showSerials && (
                        <Box>
                          <Typography component="span" sx={mobileLabelSx}>{t('store.rebateSerials.column')}</Typography>
                          {serialsCell(line)}
                        </Box>
                      )}
                      <Box>
                        <Typography component="span" sx={mobileLabelSx}>Brand / Submitted via</Typography>
                        <Typography variant="body2">{line.rebateBrand || line.brandName || '—'} · {line.rebateManager || '—'}</Typography>
                      </Box>
                      <Box>
                        <Typography component="span" sx={mobileLabelSx}>Program Window</Typography>
                        <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                          {windowStr(line.programStart, line.programEnd)}
                        </Typography>
                        {line.agreementNumber && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Agreement {line.agreementNumber}
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <Typography component="span" sx={mobileLabelSx}>Orders</Typography>
                        <Tooltip
                          title={
                            line.orderNumbers.length > 0
                              ? line.orderNumbers.slice(0, 25).join(', ') + (line.orderNumbers.length > 25 ? '…' : '')
                              : 'No order numbers recorded'
                          }
                        >
                          <Typography variant="body2" sx={{ cursor: 'default', textDecoration: 'underline dotted', display: 'inline-block' }}>
                            {num(line.orderNumbers.length)}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Stack>
            )
          ) : (
          <TableContainer>
            <Table size="small" sx={{ '& td, & th': { py: 1 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>Submitted via</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Program Window</TableCell>
                  <TableCell align="right">Rebate / Unit</TableCell>
                  <TableCell align="right">Units</TableCell>
                  <TableCell align="right">Total Claimable</TableCell>
                  {showSplit && <TableCell align="right">Vendor Funded</TableCell>}
                  {showSplit && <TableCell align="right">Store Funded</TableCell>}
                  {showTax && <TableCell align="right">{t('store.rebateTax.column')}</TableCell>}
                  {showSerials && <TableCell align="right">{t('store.rebateSerials.column')}</TableCell>}
                  <TableCell align="right">Orders</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9 + (showSplit ? 2 : 0) + (showTax ? 1 : 0) + (showSerials ? 1 : 0)}>
                      <Skeleton height={28} />
                      <Skeleton height={28} />
                      <Skeleton height={28} />
                    </TableCell>
                  </TableRow>
                ) : !hasData ? (
                  <TableRow>
                    <TableCell colSpan={9 + (showSplit ? 2 : 0) + (showTax ? 1 : 0) + (showSerials ? 1 : 0)} align="center" sx={{ py: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        No sold items matched a rebate program in this period.
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {orderSyncHint}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  report!.lines.map((line) => (
                    <TableRow key={`${line.bundleRuleId ?? line.rebatePricingRowId ?? line.rebateProgramId}-${line.sku}-${line.rebatePerUnit}-${line.storeFundedPerUnit ?? 0}`} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{line.productName}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="caption" color="text.secondary">
                            {line.sku || line.upc || '—'}
                          </Typography>
                          {!line.inCatalog && (
                            <Tooltip title="This product isn't in the Motzu catalog — matched directly from the rebate sheet">
                              <Chip label="Not in catalog" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                            </Tooltip>
                          )}
                        </Box>
                        <RebateLineSoldUnits units={line.soldUnits} />
                      </TableCell>
                      <TableCell>{line.rebateBrand || line.brandName || '—'}</TableCell>
                      <TableCell>{line.rebateManager || '—'}</TableCell>
                      <TableCell>
                        <Chip label={REBATE_TYPE_LABELS[line.rebateType]} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                          {windowStr(line.programStart, line.programEnd)}
                        </Typography>
                        {line.agreementNumber && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Agreement {line.agreementNumber}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{fmt(line.rebatePerUnit)}</TableCell>
                      <TableCell align="right">{num(line.unitsSold)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(line.totalRebate)}</TableCell>
                      {showSplit && <TableCell align="right">{fmt(line.totalVendorFunded ?? line.totalRebate)}</TableCell>}
                      {showSplit && <TableCell align="right">{fmt(line.totalStoreFunded ?? 0)}</TableCell>}
                      {showTax && <TableCell align="right">{taxCell(line.totalTaxOwed, line)}</TableCell>}
                      {showSerials && <TableCell align="right">{serialsCell(line)}</TableCell>}
                      <TableCell align="right">
                        <Tooltip
                          title={
                            line.orderNumbers.length > 0
                              ? line.orderNumbers.slice(0, 25).join(', ') + (line.orderNumbers.length > 25 ? '…' : '')
                              : 'No order numbers recorded'
                          }
                        >
                          <Typography variant="body2" sx={{ cursor: 'default', textDecoration: 'underline dotted' }}>
                            {num(line.orderNumbers.length)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </Paper>
      </PageContainer>
    </LocalizationProvider>
  );
};

export default RebateReportPage;
